import { LoadingIndicator, LoadingIndicatorType } from 'bit-ui_component_lib-b2b'
import { type ComponentsState, createInstance, type ErrorComponentsState, type PiralInstance } from 'piral-core'
import { createMenuApi } from 'piral-menu'
import { createOidcApi } from 'piral-oidc'
import * as React from 'react'
import { Link } from 'react-router-dom'
import { createConfigApi } from '../api/config/ConfigApi'
import { createWrwksFetchApi, type FetchApi } from '../api/fetch/FetchApi'
import { createWrwksQueryApi, type QueryApi } from '../api/query/QueryApi'
import { createWrwksAuthenticationApi } from '../api/user/AuthenticationApi'
import oidcClient from '../api/user/oidcClient'
import type { AuthenticationApi } from '../api/user/types'
import { type AppContentApi, createAppContentApi } from '../layout/AppContentApi'
import { createPageTopApi, type PageTopApi } from '../layout/pagetop/PageTopApi'
import { createTrayApi, type TrayApi } from '../layout/tray/TrayApi'
import { createUserMenuApi, type UserMenuApi } from '../layout/tray/usermenu/UserMenuApi'
import { createInstanceElementWithoutBreakpoints } from './createInstanceElementWithoutBreakpoints'
import { DefaultRouter } from './DefaultRouter'
import { DefaultRouteSwitch } from './DefaultRouteSwitch'
import { piletRequester, wrwksUpdateFirstLoadingStrategy } from './pilet-loading'

const WrwksMenu = React.lazy(() => import('../layout/menu/WrwMenu'))
const AppContent = React.lazy(() => import('../layout/AppContent'))
const PiralErrorBoundary = React.lazy(() => import('../error/PiralErrorBoundary'))

declare module 'piral-menu/lib/types' {
  interface PiralCustomMenuSettings {
    key: string
    /** link url or Route-Pattern e.g. `/admin/:tab` or `/data/:id/details/:tab`. For Route-Pattern see also defaultParameters */
    link: string
    title: string
    order?: number
    icon?: React.ReactNode
    /** if true link will be opened in extra tab */
    external?: boolean
    /** parent menu key */
    parent?: string
    inactive?: (pathname: string) => Promise<boolean>
    /** defaultParameter for Route-Pattern (link). e.g. `{ tab: 'MyTab' }` for link `/admin/:tab` */
    defaultParameter?: { [key: string]: string }
  }
}

declare module 'piral-core/lib/types/custom' {
  interface PiletCustomApi
    extends AuthenticationApi<Role>,
      FetchApi,
      QueryApi,
      AppContentApi,
      TrayApi,
      UserMenuApi,
      PageTopApi {}
  // NOTE additional type definitions for pilets see typings.export.d.ts
}

const instance: PiralInstance = createInstance({
  requestPilets: piletRequester,
  async: wrwksUpdateFirstLoadingStrategy,
  plugins: [
    createConfigApi(),
    createMenuApi(),
    createOidcApi(oidcClient),
    createWrwksAuthenticationApi(),
    createWrwksFetchApi(),
    createWrwksQueryApi(),
    createAppContentApi(),
    createTrayApi(),
    createUserMenuApi(),
    createPageTopApi()
  ]
})

const piralApplication: Partial<ComponentsState> = {
  LoadingIndicator: () => <LoadingIndicator type={LoadingIndicatorType.APP} />,
  ErrorInfo: (errorInfo) => <PiralErrorBoundary errorInfo={errorInfo} />,
  Layout: (props) => <AppContent {...props} />,
  MenuContainer: WrwksMenu,
  Router: (props) => <DefaultRouter {...props} />,
  RouteSwitch: (props) => <DefaultRouteSwitch {...props} />
}

const errors: Partial<ErrorComponentsState> = {
  not_found: () => (
    <div>
      <p className='error'>Die gewünschte Seite konnte leider nicht gefunden werden.</p>
      <p>
        Zurück zur <Link to='/'>Startseite</Link>.
      </p>
    </div>
  )
}

const app = createInstanceElementWithoutBreakpoints(instance, piralApplication, errors)
export { app, instance }
